import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Sparkles, MessageSquare, AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { PLAN_TIER } from "@/lib/stripe-plans";
import VoiceInput from "@/components/VoiceInput";

interface Source {
  index: number;
  title: string;
  source_type: string;
  source_url?: string | null;
}
interface Message {
  role: "user" | "assistant";
  content: string;
  sources?: Source[];
}

const MODELS = [
  { id: "gemini-flash", label: "Gemini Flash (snabb)" },
  { id: "gemini-pro", label: "Gemini Pro" },
  { id: "gpt-5-mini", label: "GPT-5 Mini" },
  { id: "gpt-5", label: "GPT-5 (avancerad)" },
] as const;

const HIDE_ON_PATHS = [
  "/", "/login", "/register", "/reset-password", "/priser", "/funktioner",
  "/om-oss", "/kontakt", "/hjalp", "/blogg", "/privacy", "/terms", "/install",
  "/unsubscribe", "/onboarding", "/ai-schemalaggning-sociala-medier", "/guide",
];

function pageContextFromPath(path: string): string {
  if (path.startsWith("/dashboard")) return "Dashboard – översikt av inlägg, statistik, streak och veckans schema.";
  if (path.startsWith("/studio")) return "Content Studio – där användaren skapar/genererar inlägg för Facebook, Instagram, LinkedIn, X och TikTok.";
  if (path.startsWith("/analytics")) return "Analytics – engagemang, bäst/sämst inlägg, ROI och plattformsjämförelse.";
  if (path.startsWith("/planner")) return "Planner – kalender med schemalagda inlägg.";
  if (path.startsWith("/marknadsforing")) return "Marknadsföring – kampanjer, målgrupper, nyhetsbrev, automation.";
  if (path.startsWith("/kundservice")) return "Kundservice – AI-inkorg, FAQ-bot, support-widget.";
  if (path.startsWith("/sales")) return "Sälj – leads, offerter, bokningar, uppföljning.";
  if (path.startsWith("/minne")) return "Företagets minne – RAG-kunskapsbas med källor.";
  if (path.startsWith("/oversikt")) return "Översikt – AI-genererad veckorapport över alla moduler.";
  if (path.startsWith("/album")) return "Album – bildbibliotek, AI-taggning.";
  if (path.startsWith("/inbox")) return "Community-inbox – kommentarer från sociala kanaler.";
  if (path.startsWith("/teams")) return "Teams – arbetsyta, godkännanden.";
  if (path.startsWith("/ideas")) return "Idéer – innehållsidéer och AI-förslag.";
  if (path.startsWith("/feeds")) return "RSS-flöden.";
  if (path.startsWith("/profile")) return "Profil – företagsinformation och röst.";
  if (path.startsWith("/subscription")) return "Prenumeration – Starter/Pro/Byrå.";
  if (path.startsWith("/aktivitet")) return "AI-aktivitetslogg.";
  return `Sida: ${path}`;
}

export default function GlobalAssistant() {
  const { user, effectivePlan } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState<string>(() => localStorage.getItem("zensio_assistant_model") ?? "gemini-flash");
  const [creditError, setCreditError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem("zensio_assistant_model", model);
  }, [model]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const context_hint = useMemo(() => pageContextFromPath(location.pathname), [location.pathname]);

  // Access rules: assistant is Pro-only (same as knowledge-query). Hide on public pages.
  const hasPro = effectivePlan ? PLAN_TIER[effectivePlan] >= PLAN_TIER.pro : false;
  const shouldHide =
    !user ||
    HIDE_ON_PATHS.some((p) => location.pathname === p || (p !== "/" && location.pathname.startsWith(p + "/")));

  if (shouldHide) return null;

  const ask = async () => {
    const q = input.trim();
    if (!q || loading) return;
    if (!hasPro) {
      toast.error("Zensio-assistenten kräver Pro-plan.");
      return;
    }
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    setCreditError(null);

    const history = messages.slice(-6).map((m) => ({ role: m.role, content: m.content }));
    const { data, error } = await supabase.functions.invoke("knowledge-query", {
      body: { question: q, history, model, context_hint },
    });
    setLoading(false);

    if (error || data?.error) {
      if (data?.error === "credit_limit_reached") {
        setCreditError(data.message);
        return;
      }
      toast.error(data?.error || "Något gick fel");
      return;
    }
    setMessages((m) => [...m, { role: "assistant", content: data.answer, sources: data.sources }]);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          className="fixed bottom-24 right-4 z-40 h-12 w-12 rounded-full shadow-lg md:bottom-6 md:right-6 md:h-14 md:w-14"
          aria-label="Öppna Zensio-assistenten"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b p-4">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-secondary" />
            Zensio-assistenten
          </SheetTitle>
          <div className="mt-2 flex items-center gap-2">
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODELS.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="shrink-0 text-[10px]">
              {context_hint.split(" – ")[0]}
            </Badge>
          </div>
          <p className="mt-1 text-left text-[11px] text-muted-foreground">
            Svarar utifrån ditt företagsminne + aktuell sida. Källor visas under svaret.
          </p>
        </SheetHeader>

        {!hasPro && (
          <div className="border-b bg-muted/40 p-4 text-sm">
            <p className="font-medium">Zensio-assistenten ingår i Pro.</p>
            <p className="mb-2 text-xs text-muted-foreground">
              Uppgradera för att chatta med AI:n på alla sidor.
            </p>
            <Button asChild size="sm" className="w-full">
              <Link to="/subscription" onClick={() => setOpen(false)}>Se planer</Link>
            </Button>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
          {messages.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <Sparkles className="mx-auto mb-2 h-6 w-6 opacity-50" />
              Ställ en fråga om ditt företag eller den här sidan.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === "user" ? "bg-secondary text-secondary-foreground" : "bg-muted"
                }`}
              >
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown>{m.content}</ReactMarkdown>
                </div>
                {m.sources && m.sources.length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-border/40 pt-2">
                    {m.sources.map((s) => (
                      <div key={s.index} className="flex items-center gap-1.5 text-[11px]">
                        <Badge variant="outline" className="h-4 px-1 text-[10px]">[{s.index}]</Badge>
                        <FileText className="h-3 w-3 opacity-60" />
                        {s.source_url ? (
                          <a href={s.source_url} target="_blank" rel="noreferrer" className="truncate hover:underline">
                            {s.title}
                          </a>
                        ) : (
                          <span className="truncate">{s.title}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-muted px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            </div>
          )}
          {creditError && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p>{creditError}</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 border-t p-3">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), ask())}
            placeholder={hasPro ? "Fråga något…" : "Kräver Pro"}
            disabled={loading || !hasPro}
          />
          <VoiceInput onTranscript={(t) => setInput((prev) => (prev ? prev + " " : "") + t)} variant="outline" />
          <Button onClick={ask} disabled={loading || !input.trim() || !hasPro} size="icon">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}